import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { EASE } from "@/components/site/Reveal";
import { useLang } from "@/i18n/LanguageContext";

const CAL_URL = "https://cal.com/saber-bellaaj-5ogauj/rdv-weyb.fr";

export const Wordmark = ({ dark = false }) => (
    <img
        src={dark ? "/assets/weyb-logo-dark.png" : "/assets/weyb-logo-main.png"}
        alt="WEYB — Your Growth Partner"
        data-testid="weyb-logo"
        className="h-10 w-auto select-none"
    />
);

const NAV_KEYS = [
    { key: "nav.home", to: "/" },
    { key: "nav.solutions", to: "/solutions" },
    { key: "nav.programmes", to: "/solutions/programmes-appui-formation" },
    { key: "nav.expertises", to: "/expertises" },
    { key: "nav.sourcing", to: "/sourcing" },
    { key: "nav.about", to: "/a-propos" },
    { key: "nav.analyses", to: "/analyses" },
    { key: "nav.contact", to: "/contact" },
];

export function LangSwitcher({ mobile = false }) {
    const { lang } = useLang();
    const location = useLocation();
    const bare = location.pathname.replace(/^\/en(?=\/|$)/, "") || "/";
    const frTo = bare;
    const enTo = bare === "/" ? "/en" : `/en${bare}`;
    const base = mobile ? "px-4 py-2 text-sm" : "px-2.5 py-1 text-[11px]";
    return (
        <div
            data-testid={mobile ? "lang-switcher-mobile" : "lang-switcher"}
            className={`flex items-center border border-gray-200 rounded-sm overflow-hidden ${mobile ? "self-start" : ""}`}
            role="group"
            aria-label="Langue / Language"
        >
            <Link
                to={frTo}
                data-testid="lang-fr"
                aria-current={lang === "fr" ? "true" : undefined}
                className={`${base} font-semibold tracking-wide transition-colors ${lang === "fr" ? "bg-weyb-ink text-white" : "text-gray-500 hover:text-weyb-ink"}`}
            >
                FR
            </Link>
            <Link
                to={enTo}
                data-testid="lang-en"
                aria-current={lang === "en" ? "true" : undefined}
                className={`${base} font-semibold tracking-wide transition-colors ${lang === "en" ? "bg-weyb-ink text-white" : "text-gray-500 hover:text-weyb-ink"}`}
            >
                EN
            </Link>
        </div>
    );
}

export default function Header() {
    const [compact, setCompact] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { t, prefix } = useLang();

    const href = (to) => (to === "/" ? prefix || "/" : `${prefix}${to}`);

    useEffect(() => {
        const onScroll = () => setCompact(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 bg-white/85 backdrop-blur-xl border-b border-gray-100 transition-all duration-300 ${compact ? "py-3" : "py-5"}`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
                <Link to={href("/")} aria-label="WEYB — Accueil" data-testid="header-logo-link">
                    <Wordmark />
                </Link>

                <nav aria-label="Navigation principale" className="hidden xl:flex items-center gap-5">
                    {NAV_KEYS.map((item) => (
                        <NavLink
                            key={item.key}
                            to={href(item.to)}
                            end={item.to === "/"}
                            data-testid={`nav-${item.key.split(".")[1]}`}
                            className={({ isActive }) =>
                                `text-[13px] whitespace-nowrap font-medium transition-colors hover:text-weyb-ink ${
                                    isActive ? "text-weyb-ink" : "text-gray-500"
                                }`
                            }
                        >
                            {t(item.key)}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <div className="hidden xl:block">
                        <LangSwitcher />
                    </div>
                    <a
                        href={CAL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="header-cta-booking"
                        className="hidden md:inline-flex items-center gap-2 whitespace-nowrap bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-5 py-2.5 rounded-sm text-sm font-semibold transition-colors"
                    >
                        {t("nav.cta")}
                        <ArrowUpRight size={16} strokeWidth={2} />
                    </a>
                    <button
                        type="button"
                        aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
                        aria-expanded={open}
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen(!open)}
                        className="xl:hidden p-2 text-weyb-ink"
                    >
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        aria-label="Navigation mobile"
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        className="xl:hidden absolute top-full inset-x-0 bg-white border-b border-gray-100 shadow-[0_24px_48px_rgba(10,15,22,0.08)]"
                    >
                        <div className="px-6 py-8 flex flex-col gap-1">
                            {NAV_KEYS.map((item, i) => (
                                <motion.div
                                    key={item.key}
                                    initial={{ opacity: 0, x: -12 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i, duration: 0.4, ease: EASE }}
                                >
                                    <NavLink
                                        to={href(item.to)}
                                        end={item.to === "/"}
                                        data-testid={`mobile-nav-${item.key.split(".")[1]}`}
                                        className="block py-3 font-display text-2xl font-medium text-weyb-ink border-b border-gray-100"
                                    >
                                        {t(item.key)}
                                    </NavLink>
                                </motion.div>
                            ))}
                            <div className="mt-6">
                                <LangSwitcher mobile />
                            </div>
                            <a
                                href={CAL_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="mobile-cta-booking"
                                className="mt-4 inline-flex items-center justify-center gap-2 bg-weyb-yellow text-weyb-ink px-5 py-3.5 rounded-sm text-sm font-semibold"
                            >
                                {t("nav.cta")}
                                <ArrowUpRight size={16} />
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
